<script setup lang="ts">
import { computed } from "vue";

import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import ListItem from "@/components/ui/ComplexList/ListItem.vue";
import TypeDot from "@/components/ui/TypeDot/TypeDot.vue";
import UserAvatar from "@/components/ui/UserAvatar/UserAvatar.vue";
import { formatTimestampFriendly, relativeTime } from "@/utils/dateFormat";

import { stickyChanges } from "../stickyChanges";
import type { DisplayableStickyChange } from "./groupChanges";
import { cardFromLogChange, isClickable } from "./groupChanges";

const props = defineProps<{
  change: DisplayableStickyChange;
  showCard?: boolean;
  expanded: boolean;
}>();
const emit = defineEmits<{ expanded: [opened: boolean] }>();

/**
 * Indicates if the change is clickable, but not expandable (only create,
 * for delete there is nothing to highlight, and all other types are expandable)
 */
const clickable = computed(() => isClickable(props.change));

function changeTitle(change: DisplayableStickyChange) {
  switch (change.kind) {
    case "create":
      return /*$t*/ "stickyChange.created";
    case "mirror":
      return /*$t*/ "stickyChange.mirrored";
    case "unmirror":
      return /*$t*/ "stickyChange.unmirrored";
    case "link":
      return /*$t*/ "stickyChange.link";
    case "unlink":
      return /*$t*/ "stickyChange.unlink";
    case "update":
      // Handle the special case where the dependent-on team was set -- here,
      // the sticky is automatically flagged at the same time, so there are
      // multiple updates, but we want to use the dependent-on-team label
      // so, it's clear to the user what happened
      if (change.fields.find((field) => field.name === "dependTeamId")) {
        return stickyChanges.dependTeamId.keyNew;
      }
      if (change.fields.length > 1) {
        return /*$t*/ "stickyChange.updated";
      }
      // If the value was previously undefined, use a slightly different label
      if ((change.fields[0].old ?? null) === null) {
        return stickyChanges[change.fields[0].name].keyNew;
      }
      return stickyChanges[change.fields[0].name].key;
    case "delete":
      return /*$t*/ "stickyChange.deleted";
  }
}

function handleExpanded(opened: boolean) {
  emit("expanded", opened);
}

/** When the user clicks a create change, emit an expanded event to enlarge the sticky */
function handleClickOnCreate() {
  if (!clickable.value) return;
  emit("expanded", !props.expanded);
}
</script>

<template>
  <ListItem
    is-collapsable
    class="list-item-sticky-activity"
    :class="{ expanded: props.expanded }"
    :expanded
    :clickable
    @expanded="handleExpanded"
    @click="handleClickOnCreate"
  >
    <!-- Title & avatar [eg. (J) John Doe changed the iteration] -->
    <div class="change-title">
      <UserAvatar :user="change.user" size="small" aria-hidden="true" />
      <BaseTooltip only-show-on-overflow class="change-description">
        <template #content>
          <I18nT :keypath="changeTitle(change)" scope="global" tag="span">
            <template #user> {{ change.user.name }} </template>
          </I18nT>
        </template>
        <I18nT :keypath="changeTitle(change)" scope="global" tag="span">
          <template #user> {{ change.user.name }} </template>
        </I18nT>
      </BaseTooltip>
    </div>

    <template #subtitle>
      <div class="subtitle">
        <!-- (optional) sticky note icon and text -->
        <div v-if="showCard" class="change-title">
          <TypeDot
            size="medium"
            :color="cardFromLogChange(change).color"
            class="sticky-note-icon"
          />
          <BaseTooltip only-show-on-overflow class="change-description">
            <template #content>{{ cardFromLogChange(change).text }}</template>
            {{ cardFromLogChange(change).text }}
          </BaseTooltip>
        </div>

        <!-- (relative) timestamp (wrapped in a div so the tooltip trigger isn't full-width) -->
        <div>
          <BaseTooltip position="bottom" tag="span">
            {{ relativeTime(change.timestamp) }}
            <template #content>
              {{ formatTimestampFriendly(change.timestamp) }}
            </template>
          </BaseTooltip>
        </div>
      </div>
    </template>

    <!-- Change details (collapsable) -->
    <template v-if="change.expandable" #collapsedContent>
      <div class="change-details fields" aria-live="polite" @click.stop>
        <div
          v-for="field in change.fields"
          :key="field.name"
          class="change-elem field"
          :class="{ multi: change.fields.length > 1 }"
        >
          <div v-if="change.fields.length > 1" class="diff-item-title">
            {{ $t(stickyChanges[field.name].keyField) }}
          </div>
          <div class="field field-wrapper">
            <component
              :is="stickyChanges[field.name].component"
              :field
              :sticky-id="change.stickyId"
            />
          </div>
        </div>
      </div>
    </template>
  </ListItem>
</template>

<!-- Styles are not scoped so the styles imported from changes are applied properly -->
<style lang="scss">
@use "@/styles/mixins/utils";
@use "@/styles/mixins/typography";
@use "@/styles/variables/colors";
@use "changes";
@use "activityListItem";

.list-item.list-item-sticky-activity {
  @include activityListItem.sticky-changes-border-left;

  .content {
    gap: 12px;
  }
}

.list-item-sticky-activity {
  @include changes.sticky-change;

  // When expanded, the item should appear active, but not with the usual
  // active styles, which are a bit too heavy for this use case
  &.expanded {
    background-color: colors.$background-grey-light;
  }

  .change-title {
    display: flex;
    align-items: center;
    gap: 12px;

    .sticky-note-icon {
      margin: 2px;
    }

    .change-description {
      @include utils.ellipsis;
    }
  }

  .subtitle {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .diff-item-title {
    @include typography.small("semi-bold");
  }
}
</style>
